import { SITE_CONFIG } from "./seoConfig";

/**
 * Shared JSON-LD structured data schemas
 */

/** Website schema */
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_CONFIG.name,
  url: SITE_CONFIG.url,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_CONFIG.url}/products?search={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

/** Organization schema */
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_CONFIG.name,
  url: SITE_CONFIG.url,
  logo: {
    "@type": "ImageObject",
    url: SITE_CONFIG.logo,
    width: 512,
    height: 512,
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: SITE_CONFIG.supportPhone,
      contactType: "customer support",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
    },
  ],
  email: SITE_CONFIG.supportEmail,
  address: {
    "@type": "PostalAddress",
    addressLocality: "Lucknow",
    addressRegion: "Uttar Pradesh",
    addressCountry: "IN",
  },
  sameAs: [
    "https://www.linkedin.com/company/real-flame-india/",
    "https://www.instagram.com/realflame_stoves",
    "https://youtube.com/@realflame-stoves"
  ],
};

/**
 * Generate product JSON-LD schema
 */
export function productSchema({ name, description, image, price, mrp, sku, url, brand = "RealFlame", rating, reviewCount }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    image,
    sku,
    brand: {
      "@type": "Brand",
      name: brand,
    },
    url,
  };

  if (price && typeof price === "number") {
    schema.offers = {
      "@type": "Offer",
      url,
      priceCurrency: "INR",
      price: price,
      priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 *  1500)
        .toISOString()
        .split("T")[0],
      itemCondition: "https://schema.org/NewCondition",
      availability: "https://schema.org/InStock",
    };
    if (mrp && mrp > price) {
      schema.offers.priceSpecification = {
        "@type": "PriceSpecification",
        price: mrp,
        priceCurrency: "INR",
      };
    }
  }

  if (rating && reviewCount) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: rating,
      reviewCount: reviewCount,
      bestRating: 5,
      worstRating: 1,
    };
  }

  return schema;
}

/**
 * Generate breadcrumb JSON-LD schema
 */
export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * FAQPage schema for factory-ready PNG stoves search intent (Google AI Overviews)
 */
export const pngStoveFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Why choose factory-ready PNG stoves over LPG-to-PNG conversion?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Factory-ready RealFlame PNG stoves are pre-configured specifically for piped natural gas (PNG) with wider burner jets and lower-pressure calibration straight out of the box, eliminating the need for manual nozzle modifications, swaps, or conversions.",
      },
    },
    {
      "@type": "Question",
      name: "Is any nozzle modification required for RealFlame PNG stoves?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No. RealFlame PNG stoves are engineered specifically for PNG gas pipelines with optimized air-gas ratios, requiring no nozzle swapping, drilling, or LPG-to-PNG conversion.",
      },
    },
    {
      "@type": "Question",
      name: "Are RealFlame PNG stoves compliant with safety standards for piped networks?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, RealFlame manufactures direct PNG stoves in compliance with applicable safety and performance standards for piped gas networks across India.",
      },
    },
  ],
};

